import { ModuleWithProviders, NgModule } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { LoggerConfiguration, LogsInitConfiguration } from '@datadog/browser-logs';

import { NinetyFrontEndEnvironment } from '@ninety/mobile/platform/environments';

import { DatadogLogger } from './datadog-logger';
import { DatadogLoggerFactory } from './datadog-logger-factory';
import { DatadogLogsService } from './datadog-logs.service';
import { DatadogReservedTagsRequired } from './types';

type RootOptions = {
  name: string;
  globalContext: DatadogReservedTagsRequired & {
    /** Whether the environment is local or in a deployed instance */
    isLocal: boolean;
    region: string;
    /**
     * Distinguish native-app users from mobile-web users (app-vs-browser). iOS-vs-Android is already covered by
     * Datadog's automatic device/OS tags.
     */
    platform: 'native' | 'web';
  };
  initConf: LogsInitConfiguration;
  conf?: LoggerConfiguration;
};

type ChildOptions = Omit<RootOptions, 'initConf' | 'globalContext'>;

@NgModule()
export class DatadogLoggerModule {
  static forRootWithEnvironment(
    environment: NinetyFrontEndEnvironment,
    initConf?: LogsInitConfiguration
  ): ModuleWithProviders<DatadogLoggerModule> {
    return this.forRoot({
      name: 'AppModule',
      globalContext: {
        env: environment.env,
        isLocal: environment.hostedEnvironment === 'localhost',
        platform: Capacitor.isNativePlatform() ? 'native' : 'web',
        region: environment.region,
        service: environment.service,
        version: environment.appVersion,
      },
      initConf: {
        // Return false to omit log
        // Mutate to modify, see redact example
        beforeSend: log => {
          /** DO NOT LOG TO CONSOLE IN THIS METHOD, IT WILL CAUSE AN INFINITE LOOP */
          if (!environment.production) {
            if (
              log.message?.startsWith('Angular is running in development mode') ||
              log.message?.startsWith('@ngrx/store: The feature name') ||
              log.message?.includes('[webpack-dev-server]')
            ) {
              return false;
            }
          }
          // We want to use level for consistency
          log['level'] = log.status;
          delete (log as { status?: string }).status;
          return true;

          // Eg. of redaction
          // log.view.url = log.view.url.replace(/email=[^&]*/, 'email=REDACTED');
        },
        clientToken: environment.dataDogClientToken,
        env: environment.env,
        forwardConsoleLogs: initConf?.forwardConsoleLogs ? initConf.forwardConsoleLogs : 'all',
        forwardErrorsToLogs: initConf?.forwardErrorsToLogs ? initConf.forwardErrorsToLogs : true,
        sessionSampleRate: initConf?.sessionSampleRate ? initConf.sessionSampleRate : 100,
        site: initConf?.site ? initConf.site : 'datadoghq.com',
        telemetrySampleRate: initConf?.telemetrySampleRate ? initConf.telemetrySampleRate : 0,
      },
    });
  }

  static forRoot(options: RootOptions): ModuleWithProviders<DatadogLoggerModule> {
    return {
      ngModule: DatadogLoggerModule,
      providers: [
        {
          provide: DatadogLogger,
          useFactory: () => DatadogLoggerFactory(options.name, options.conf),
        },
        {
          provide: DatadogLogsService,
          useFactory: () => {
            const service = new DatadogLogsService().init(options.initConf);
            // Set per property rather than replacing the context. This factory runs during DI
            // construction, while `main.ts` writes native_app_version from a promise that can
            // resolve either side of it — a replace here drops it when it landed first.
            Object.entries(options.globalContext).forEach(([key, value]) =>
              service.setGlobalContextProperty(key, value)
            );

            return service;
          },
        },
      ],
    };
  }

  static forFeature(options: ChildOptions): ModuleWithProviders<DatadogLoggerModule> {
    return {
      ngModule: DatadogLoggerModule,
      providers: [
        {
          provide: DatadogLogger,
          useFactory: () => DatadogLoggerFactory(options.name, options.conf),
        },
      ],
    };
  }
}
